import React, { useEffect, useState } from "react";
import { X, UserPlus, GitMerge, Eye, EyeOff } from "lucide-react";
import {
  listCoachingAthletes,
  cloneProgram,
  insertFromProgram,
  listPrograms,
} from "../../lib/api";
import { toast } from "sonner";

const MODE_NEW = "new";
const MODE_INSERT = "insert";

/**
 * Modal to copy a generic library program into an athlete's plan.
 *
 * Two modes:
 *   "new"    — clone the (full or partial) library program as a fresh 1:1
 *              program for the athlete. The athlete ends up with another
 *              program in their list. (Original behaviour.)
 *   "insert" — copy a SLICE of library weeks INTO one of the athlete's
 *              existing 1:1 programs, starting at a chosen week. Existing
 *              weeks at/after that slot are pushed down. Same workflow
 *              Natalie uses to stitch e.g. weeks 5-12 of an MTSS template
 *              onto the end of an athlete's current build block.
 */
export default function AssignProgramModal({ program, onClose, onAssigned, lockedAthleteId }) {
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [athleteId, setAthleteId] = useState(lockedAthleteId || "");
  const [title, setTitle] = useState("");
  const [startDate, setStartDate] = useState("");
  const [targetRace, setTargetRace] = useState("");
  // Source week range — default to "all"
  const totalWeeks = program?.weeks?.length || program?.total_weeks || 1;
  const [fromWeek, setFromWeek] = useState(1);
  const [toWeek, setToWeek] = useState(totalWeeks);
  // Mode + insert-mode state
  const [mode, setMode] = useState(MODE_NEW);
  const [targetPrograms, setTargetPrograms] = useState([]);
  const [targetProgramId, setTargetProgramId] = useState("");
  const [insertAt, setInsertAt] = useState(1);
  // Read-only mini-grid of the weeks the coach is about to copy. Lets her
  // confirm "yes, this is the build block I want" before committing.
  const [showSlicePreview, setShowSlicePreview] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await listCoachingAthletes();
        setAthletes(data);
      } catch {
        toast.error("Couldn't load athletes");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // When the coach picks an athlete, pre-fetch their existing 1:1
  // programs so the "Insert into" picker has something to show.
  useEffect(() => {
    if (!athleteId) {
      setTargetPrograms([]);
      setTargetProgramId("");
      return;
    }
    (async () => {
      try {
        const list = await listPrograms({
          program_type: "one_to_one",
          athlete_id: athleteId,
        });
        setTargetPrograms(list || []);
        // Auto-select if there's exactly one
        if (list && list.length === 1) {
          setTargetProgramId(list[0].id);
          setInsertAt((list[0].weeks?.length || 0) + 1);
        }
      } catch {
        setTargetPrograms([]);
      }
    })();
  }, [athleteId]);

  // When the coach picks a target program, default insert-at-week to
  // "after that program's last week" (the most-natural append behaviour).
  useEffect(() => {
    const tp = targetPrograms.find((p) => p.id === targetProgramId);
    if (tp) {
      setInsertAt((tp.weeks?.length || 0) + 1);
    }
  }, [targetProgramId, targetPrograms]);

  const selected = athletes.find((a) => a.id === athleteId);
  const targetProgram = targetPrograms.find((p) => p.id === targetProgramId);
  const sliceCount = Math.max(0, (toWeek || 0) - (fromWeek || 0) + 1);
  const sliceValid =
    fromWeek >= 1 && toWeek >= fromWeek && toWeek <= totalWeeks;

  const submit = async (e) => {
    e.preventDefault();
    if (!athleteId) {
      toast.error("Pick an athlete first");
      return;
    }
    if (!sliceValid) {
      toast.error("Week range is invalid");
      return;
    }
    setSubmitting(true);
    try {
      if (mode === MODE_INSERT) {
        if (!targetProgramId) {
          toast.error("Pick which program to insert into");
          setSubmitting(false);
          return;
        }
        const p = await insertFromProgram(targetProgramId, {
          source_program_id: program.id,
          from_week: fromWeek,
          to_week: toWeek,
          insert_at_week: insertAt,
        });
        toast.success(
          `Added ${sliceCount} week${sliceCount === 1 ? "" : "s"} to ${selected?.name || "athlete"}`
        );
        onAssigned?.(p);
      } else {
        const body = {
          athlete_id: athleteId,
          title: title.trim() || undefined,
          start_date: startDate || undefined,
          target_race: targetRace.trim() || undefined,
          from_week: fromWeek !== 1 || toWeek !== totalWeeks ? fromWeek : undefined,
          to_week: fromWeek !== 1 || toWeek !== totalWeeks ? toWeek : undefined,
        };
        const p = await cloneProgram(program.id, body);
        onAssigned?.(p);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Couldn't assign program";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[var(--tir-ink)]/50"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid="assign-program-modal"
    >
      <form
        onSubmit={submit}
        className="bg-[var(--tir-ivory)] border-2 border-[var(--tir-ink)] w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto"
        data-testid="assign-program-form"
      >
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-[var(--tir-burgundy)] font-semibold mb-1">
              Assign to athlete
            </div>
            <h3 className="font-serif-display text-2xl text-[var(--tir-ink)] leading-tight">
              {program.title}
            </h3>
            <p className="text-xs text-[var(--tir-muted)] mt-1">
              {totalWeeks} week{totalWeeks === 1 ? "" : "s"} in this template — copy all or a slice.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[var(--tir-muted)] hover:text-[var(--tir-ink)] p-1"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
              Athlete
            </label>
            {lockedAthleteId ? (
              <div
                className="w-full bg-[var(--tir-ivory)]/40 border border-[var(--tir-border)] py-2 px-3 text-base text-[var(--tir-ink)]"
                data-testid="assign-athlete-locked"
              >
                {athletes.find((a) => a.id === lockedAthleteId)?.name || "—"}
              </div>
            ) : (
              <select
                required
                value={athleteId}
                onChange={(e) => {
                  setAthleteId(e.target.value);
                  const a = athletes.find((x) => x.id === e.target.value);
                  if (a && !title) {
                    setTitle(`${a.name} — ${program.title}`);
                  }
                  if (a && !targetRace && a.target_race) {
                    setTargetRace(a.target_race);
                  }
                }}
                data-testid="assign-athlete-select"
                className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-2 px-3 text-base focus:outline-none"
              >
                <option value="">{loading ? "Loading…" : "— Choose athlete —"}</option>
                {athletes.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                    {a.email ? ` · ${a.email}` : ""}
                  </option>
                ))}
              </select>
            )}
            {athletes.length === 0 && !loading && (
              <div className="text-xs text-[var(--tir-muted)] mt-2 italic">
                No athletes yet. Add one from the 1:1 Athletes tab first.
              </div>
            )}
          </div>

          {/* Week range — applies to BOTH modes */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold">
                Weeks from this template
              </span>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setShowSlicePreview((v) => !v)}
                  data-testid="assign-slice-preview-btn"
                  className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-navy)] font-semibold"
                  title="Show a read-only mini-grid of the weeks you're about to copy"
                >
                  {showSlicePreview ? <EyeOff size={11} /> : <Eye size={11} />}
                  {showSlicePreview ? "Hide preview" : "Preview"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setFromWeek(1);
                    setToWeek(totalWeeks);
                  }}
                  data-testid="assign-week-range-all"
                  className="text-[10px] tracking-[0.18em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] font-semibold"
                >
                  All ({totalWeeks})
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2 text-[12px]">
              <span className="font-mono-tir text-[var(--tir-muted)]">W</span>
              <input
                type="number"
                min={1}
                max={totalWeeks}
                value={fromWeek}
                onChange={(e) => setFromWeek(Math.max(1, Number(e.target.value) || 1))}
                data-testid="assign-from-week-input"
                className="w-16 bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-2 py-1 text-center font-mono-tir focus:outline-none"
              />
              <span className="text-[var(--tir-muted)]">→ W</span>
              <input
                type="number"
                min={fromWeek}
                max={totalWeeks}
                value={toWeek}
                onChange={(e) => setToWeek(Math.min(totalWeeks, Number(e.target.value) || totalWeeks))}
                data-testid="assign-to-week-input"
                className="w-16 bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-2 py-1 text-center font-mono-tir focus:outline-none"
              />
              <span className="text-[10px] text-[var(--tir-muted)] italic ml-1">
                ({sliceCount} week{sliceCount === 1 ? "" : "s"})
              </span>
            </div>
            {showSlicePreview && (
              <SlicePreview
                program={program}
                fromWeek={fromWeek}
                toWeek={toWeek}
              />
            )}
          </div>

          {/* Mode toggle */}
          <div className="border-t border-[var(--tir-border)] pt-4">
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMode(MODE_NEW)}
                data-testid="assign-mode-new"
                className={`px-3 py-2 text-[10px] tracking-[0.18em] uppercase font-semibold border transition-colors text-left ${
                  mode === MODE_NEW
                    ? "bg-[var(--tir-burgundy)] text-white border-[var(--tir-burgundy)]"
                    : "bg-white text-[var(--tir-ink)] border-[var(--tir-border)] hover:border-[var(--tir-ink)]"
                }`}
              >
                <UserPlus size={12} className="inline mr-1.5" />
                New program
              </button>
              <button
                type="button"
                onClick={() => setMode(MODE_INSERT)}
                data-testid="assign-mode-insert"
                disabled={!athleteId || targetPrograms.length === 0}
                title={
                  !athleteId
                    ? "Pick an athlete first"
                    : targetPrograms.length === 0
                    ? "Athlete has no existing 1:1 programs to insert into"
                    : ""
                }
                className={`px-3 py-2 text-[10px] tracking-[0.18em] uppercase font-semibold border transition-colors text-left disabled:opacity-40 disabled:cursor-not-allowed ${
                  mode === MODE_INSERT
                    ? "bg-[var(--tir-navy)] text-white border-[var(--tir-navy)]"
                    : "bg-white text-[var(--tir-ink)] border-[var(--tir-border)] hover:border-[var(--tir-ink)]"
                }`}
              >
                <GitMerge size={12} className="inline mr-1.5" />
                Add to existing
              </button>
            </div>
          </div>

          {mode === MODE_NEW && (
            <>
              <div>
                <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                  Program title (optional)
                </label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={
                    selected
                      ? `${selected.name} — ${program.title}`
                      : "Defaults to 'Athlete — Program title'"
                  }
                  data-testid="assign-title-input"
                  className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-2 px-3 text-base focus:outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                    Start date (optional)
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    data-testid="assign-start-date-input"
                    className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-2 px-3 text-base focus:outline-none font-mono-tir"
                  />
                </div>
                <div>
                  <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                    Target race (optional)
                  </label>
                  <input
                    value={targetRace}
                    onChange={(e) => setTargetRace(e.target.value)}
                    placeholder="Boston Marathon"
                    data-testid="assign-target-race-input"
                    className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-2 px-3 text-base focus:outline-none"
                  />
                </div>
              </div>
            </>
          )}

          {mode === MODE_INSERT && (
            <>
              <div>
                <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                  Insert into which existing program?
                </label>
                <select
                  required
                  value={targetProgramId}
                  onChange={(e) => setTargetProgramId(e.target.value)}
                  data-testid="assign-target-program-select"
                  className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-navy)] py-2 px-3 text-base focus:outline-none"
                >
                  <option value="">— Choose program —</option>
                  {targetPrograms.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.title || "(untitled)"} · {p.weeks?.length || 0} wk
                    </option>
                  ))}
                </select>
              </div>
              {targetProgram && (
                <div>
                  <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                    Starting at week
                    <span className="ml-2 normal-case tracking-normal text-[10px] italic text-[var(--tir-muted)]/70 font-normal">
                      — existing weeks at/after this slot are pushed down
                    </span>
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="font-mono-tir text-sm text-[var(--tir-muted)]">W</span>
                    <input
                      type="number"
                      min={1}
                      max={(targetProgram.weeks?.length || 0) + 1}
                      value={insertAt}
                      onChange={(e) => setInsertAt(Math.max(1, Number(e.target.value) || 1))}
                      data-testid="assign-insert-at-input"
                      className="w-20 bg-white border border-[var(--tir-border)] focus:border-[var(--tir-navy)] py-2 px-3 text-center font-mono-tir focus:outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => setInsertAt((targetProgram.weeks?.length || 0) + 1)}
                      data-testid="assign-insert-at-append"
                      className="text-[10px] tracking-[0.18em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-navy)] font-semibold px-2 py-1 border border-[var(--tir-border)] hover:border-[var(--tir-navy)]"
                    >
                      Append (after W{targetProgram.weeks?.length || 0})
                    </button>
                  </div>
                  <div className="mt-2 text-[11px] text-[var(--tir-muted)] italic leading-snug" data-testid="assign-insert-preview">
                    Result: {targetProgram.title} will go from {targetProgram.weeks?.length || 0} →{" "}
                    {Math.max(targetProgram.weeks?.length || 0, insertAt - 1) + sliceCount} weeks.
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 mt-7 pt-5 border-t border-[var(--tir-border)]">
          <button
            type="button"
            onClick={onClose}
            className="text-sm tracking-[0.2em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-ink)] px-3 py-2"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={
              submitting ||
              !athleteId ||
              !sliceValid ||
              (mode === MODE_INSERT && !targetProgramId)
            }
            data-testid="confirm-assign-btn"
            className={`inline-flex items-center gap-2 text-white px-5 py-2.5 text-sm tracking-[0.22em] uppercase font-semibold transition-colors disabled:opacity-40 ${
              mode === MODE_INSERT
                ? "bg-[var(--tir-navy)] hover:bg-[#152a45]"
                : "bg-[var(--tir-burgundy)] hover:bg-[var(--tir-burgundy-dark)]"
            }`}
          >
            {mode === MODE_INSERT ? <GitMerge size={14} /> : <UserPlus size={14} />}
            {submitting
              ? mode === MODE_INSERT
                ? "Inserting…"
                : "Assigning…"
              : mode === MODE_INSERT
              ? `Insert ${sliceCount} wk → W${insertAt}`
              : "Assign"}
          </button>
        </div>
      </form>
    </div>
  );
}



/**
 * Compact, read-only preview of a slice of weeks from the library program.
 * Each row = one week. Columns = the 7 days starting from the template's
 * `start_day` (defaults Mon). A workout shows as `type` + a short label so
 * the coach can recognise the block. Mobility/strength rows show "Mob"
 * or "Str (3 ex)" so the slice's flavour is obvious at a glance.
 *
 * Renders inside the AssignProgramModal so it doesn't need any API call
 * of its own — the source program's full weeks are already on the prop.
 */
function SlicePreview({ program, fromWeek, toWeek }) {
  const weeks = (program?.weeks || []).filter(
    (w) =>
      typeof w.week_number === "number" &&
      w.week_number >= fromWeek &&
      w.week_number <= toWeek
  );
  if (!weeks.length) {
    return (
      <div
        className="mt-3 border border-[var(--tir-border)] bg-white p-3 text-center text-[11px] text-[var(--tir-muted)] italic"
        data-testid="assign-slice-preview"
      >
        Pick a valid week range to preview the slice.
      </div>
    );
  }
  const dayLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  return (
    <div
      className="mt-3 border border-[var(--tir-border)] bg-white p-2 max-h-[260px] overflow-y-auto"
      data-testid="assign-slice-preview"
    >
      <table className="w-full border-collapse">
        <thead>
          <tr className="text-[9px] tracking-[0.18em] uppercase text-[var(--tir-muted)]">
            <th className="text-left py-1 pr-2 font-semibold w-8">Wk</th>
            {dayLabels.map((d) => (
              <th key={d} className="text-left py-1 px-1 font-semibold">
                {d}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((w) => (
            <tr
              key={w.id || w.week_number}
              className="border-t border-[var(--tir-border)]/40 align-top"
              data-testid={`slice-preview-row-${w.week_number}`}
            >
              <td className="py-1 pr-2 font-mono-tir text-[11px] text-[var(--tir-ink)]">
                W{w.week_number}
                {w.is_recovery && (
                  <span className="block text-[8px] text-[var(--tir-navy)] tracking-[0.18em]">
                    REC
                  </span>
                )}
              </td>
              {(w.days || []).slice(0, 7).map((d, di) => (
                <td
                  key={d.id || di}
                  className="py-1 px-1 text-[10px] text-[var(--tir-ink)] align-top"
                >
                  {(d.workouts || []).length === 0 ? (
                    <span className="text-[var(--tir-muted)]/60">·</span>
                  ) : (
                    <div className="space-y-0.5">
                      {(d.workouts || []).map((wo, wi) => (
                        <div
                          key={wo.id || wi}
                          className="truncate leading-tight"
                          title={summarizeWorkout(wo)}
                        >
                          {summarizeWorkout(wo)}
                        </div>
                      ))}
                    </div>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function summarizeWorkout(wo) {
  if (!wo) return "—";
  const t = (wo.type || "").toLowerCase();
  const dist = wo.duration_or_distance ? String(wo.duration_or_distance).trim() : "";
  if (t === "mobility") return "Mob";
  if (t === "strength" || t === "strength_run") {
    const n = (wo.strength_blocks || []).reduce(
      (acc, b) => acc + (b.exercises || []).length,
      0
    );
    return n ? `Str (${n} ex)` : "Str";
  }
  if (t === "rest") return "Rest";
  // For run/long/easy/tempo/speed/race: show type + abbreviated distance
  const label = t ? t[0].toUpperCase() + t.slice(1) : "—";
  return dist ? `${label} · ${dist}` : label;
}
